import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Pressable, ScrollView } from 'react-native-web';
import { ServiceItem } from '../ServiceItem/ServiceItem';
import {
  BodyText,
  Button,
  Stack,
} from '../../../ui';

import { BasicSearchIcon } from '../../../ui/Icons';
import { useIsMobile } from '../../../../hooks';
import { ServiceGroupHeader } from './ServiceGroupHeader';
import { SearchServiceModal } from '../ServiceSearchModal/ServiceSearchModal';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { Service } from '../../../../store/ProfileServiceGroups';
import analytics from '../../../../modules/analytics';
import HeaderServiceButton from '../HeaderServices/HeaderServiceButton';
import { ServiceListBreadCrumbs } from './ServiceListBreadCrumbs';
import { useServiceTruncationHandler } from './hooks';
import { ProviderAllServiceReviewsSummaryState } from '../../../../store/ProviderReviewsSummary.model';

type ServiceListProps = {
  profile: ClientProviderProfile;
  onRatingsPhotoClick: (service: Service) => void;
  hasMarketplaceProfile: boolean;
  reviewsSummary: ProviderAllServiceReviewsSummaryState;
};

export const ServiceList: React.FC<ServiceListProps> = ({
  profile,
  onRatingsPhotoClick,
  hasMarketplaceProfile,
  reviewsSummary,
}) => {
  const scrollDivRefs = useRef([]);
  const preSelectedCategoryIndex = useRef(null);
  const isMobile = useIsMobile();

  const [showSearchModal, setShowServiceModal] = useState(false);
  const currentProId = profile.id;

  const {
    serviceGroupsTrimmed, isShowingAllServices, handleShowAllServices, allServiceGroups,
  } = useServiceTruncationHandler({ currentProId, hasMarketplaceProfile });

  if (allServiceGroups.length > 1) {
    scrollDivRefs.current = Array(serviceGroupsTrimmed.length)
      .fill(null)
      .map((_, i) => scrollDivRefs.current[i] || React.createRef());
  }
  const showSeeAllServiceButton = (
    !isShowingAllServices && isMobile
  );

  const handleSearchClick = () => {
    analytics.track('service_list_search_service_click');
    setShowServiceModal(true);
  };

  const handleModalClose = () => {
    analytics.track('service_list_search_service_closed');
    setShowServiceModal(false);
  };

  const handdleCategoryButtonsClick = useCallback((index: number) => {
    if (showSeeAllServiceButton) {
      handleShowAllServices();
      preSelectedCategoryIndex.current = index;
    }
    analytics.track('client_proprofile_service_list_category_clicked', {
      category: serviceGroupsTrimmed[index].name,
    });
    scrollDivRefs?.current[index]?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [
    handleShowAllServices,
    serviceGroupsTrimmed,
    showSeeAllServiceButton,
  ]);

  useEffect(() => {
    if (preSelectedCategoryIndex.current !== null) {
      handdleCategoryButtonsClick(preSelectedCategoryIndex.current);
      preSelectedCategoryIndex.current = null;
    }
  }, [handdleCategoryButtonsClick]);

  return (
    <Stack testID="service-list-section">
      <Stack>
        {serviceGroupsTrimmed.length > 1 && (
          <Box paddingBottom={24} testID="service-list-search-box">
            {allServiceGroups.length > 1 && isMobile && (
              <ScrollView horizontal>
                <Inline
                  style={{ gap: 8 }}
                  paddingY={8}
                  paddingX={isMobile ? 24 : 0}
                >
                  <Pressable onPress={handleSearchClick}>
                    <Inline
                      space={8}
                      paddingY={10}
                      paddingX={10}
                      borderWidth={0.5}
                      borderRadius={4}
                      borderStyle="solid"
                      borderColor={BrandColor.Silver}
                      minWidth={85}
                      color={BrandColor.WhiteWash}
                    >
                      <BasicSearchIcon height={16} width={16} color={BrandColor.Metal} />
                      <BodyText
                        textAlign="center"
                        appearance="small"
                      >
                        Search Services
                      </BodyText>
                    </Inline>
                  </Pressable>
                  {allServiceGroups.map((serviceGroup, index) => (
                    <HeaderServiceButton
                      category={serviceGroup.name || 'Services'}
                      key={serviceGroup.name}
                      onCategoryClick={() => handdleCategoryButtonsClick(index)}
                    />
                  ))}
                </Inline>
              </ScrollView>
            )}
          </Box>
        )}
        <Stack space={8}>
          {serviceGroupsTrimmed.map((serviceGroup, index) => (
            <Stack paddingX={isMobile ? 24 : 0} key={serviceGroup.id}>
              <ServiceGroupHeader
                serviceCount={serviceGroup?.services?.length}
                serviceName={serviceGroup?.name}
                ref={scrollDivRefs.current[index]}
              />
              {serviceGroup?.services?.map(service => (
                <ServiceItem
                  key={service.id}
                  isOnlineBookable={service.is_online_bookable}
                  providerId={currentProId}
                  service={service}
                  providerPhone={profile.public_phone_number}
                  onRatingsPhotosClick={onRatingsPhotoClick}
                  serviceReviewSummary={reviewsSummary?.[service.id]}
                />
              ))}
            </Stack>
          ))}
        </Stack>
        {showSeeAllServiceButton && (
          <Box
            testID="see-all-services-button"
            paddingTop={32}
            paddingX={isMobile ? 24 : 0}
          >
            <Button
              appearance="primary-neue"
              onClick={handleShowAllServices}
            >
              See all services
            </Button>
          </Box>
        )}
      </Stack>
      {showSearchModal && (
        <SearchServiceModal
          profile={profile}
          serviceGroups={allServiceGroups}
          onCancel={handleModalClose}
        />
      )}
      <Box
        paddingTop={showSeeAllServiceButton ? 24 : 32}
        paddingBottom={32}
        paddingX={isMobile ? 24 : 0}
        testID="service-list-breadcrumbs"
      >
        <ServiceListBreadCrumbs provider={profile} paddingTop={0} />
      </Box>
    </Stack>
  );
};
